import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material';
import { AnnouncementForm } from './form';
import {
  getAnnouncement,
  updateAnnouncement,
  type PublicAnnouncement,
} from '../../lib/announcements-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

export function AnnouncementEditPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const [initial, setInitial] = useState<PublicAnnouncement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const a = await getAnnouncement(id);
        setInitial(a);
      } catch (e) {
        setLoadError(extractApiError(e).message);
      }
    })();
  }, [id]);

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }
  if (!initial) {
    return (
      <Box sx={{ textAlign: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AnnouncementForm
        initial={initial}
        isEdit
        submitting={submitting}
        onCancel={() => nav('/announcements')}
        onSubmit={async (values) => {
          if (!id) return;
          setSubmitting(true);
          try {
            // Strip publish_now — handled separately via the Publish button on the list
            const { publish_now: _ignored, ...patch } = values;
            void _ignored;
            await updateAnnouncement(id, patch);
            setToast({ severity: 'success', message: t('ann.saved') });
            setTimeout(() => nav('/announcements'), 400);
          } catch (e) {
            setToast({ severity: 'error', message: extractApiError(e).message });
            throw e;
          } finally {
            setSubmitting(false);
          }
        }}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
